import React, { FC, useEffect, useState } from 'react';
import { WarningIcon } from 'assets/icons';
import { CommunicationsMessage } from '../communications.model';

interface CommunicationStripProps {
  message: CommunicationsMessage;
  onCtaClick: () => void;
}

const CommunicationStrip: FC<CommunicationStripProps> = ({ message, onCtaClick }) => {
  const backgroundColor = message.color || '#EFF4FF';
  const fontColor = message.fontColor || '#175CD3';
  const [heading, ...bodyLines] = message.lines ?? [];
  const imageUrl = message.imageUrl?.trim() || null;
  const [hasImageError, setHasImageError] = useState(false);

  useEffect(() => {
    setHasImageError(false);
  }, [imageUrl]);

  const showRemoteImage = Boolean(imageUrl) && !hasImageError;
  const showFallbackIcon = !showRemoteImage && Boolean(imageUrl || heading);

  const renderIcon = () => {
    if (showRemoteImage) {
      return (
        <img src={imageUrl} alt="" className="h-5 w-5 shrink-0 object-contain" onError={() => setHasImageError(true)} />
      );
    }

    if (showFallbackIcon) {
      return (
        <WarningIcon className="path-stroke-current h-5 w-5 shrink-0" style={{ color: fontColor }} aria-hidden="true" />
      );
    }

    return null;
  };

  return (
    <div
      className="flex w-full items-start justify-between gap-3 px-4 py-2.5"
      style={{ backgroundColor, color: fontColor }}
      role="status"
      aria-label={heading || 'Account notice'}>
      <div className="flex min-w-0 flex-1 items-start gap-3">
        <div className="mt-0.5 shrink-0">{renderIcon()}</div>
        <div
          className="flex min-w-0 flex-1 flex-col gap-0.5 whitespace-normal break-words"
          style={{ color: fontColor }}>
          {heading ? <div className="text-sbase font-semibold">{heading}</div> : null}
          {bodyLines.map((line, index) => (
            <div key={`communications-strip-line-${message.id}-${index}`} className="text-sbase font-normal">
              {line}
            </div>
          ))}
        </div>
      </div>
      {message.ctaText ? (
        <button
          type="button"
          id={`communications-strip-cta-${message.id}`}
          className="shrink-0 cursor-pointer self-center text-sbase font-semibold underline"
          style={{ color: fontColor }}
          onClick={onCtaClick}
          aria-label={message.ctaText}
          tabIndex={0}
          onKeyDown={event => {
            if (event.key === 'Enter' || event.key === ' ') {
              event.preventDefault();
              onCtaClick();
            }
          }}>
          {message.ctaText}
        </button>
      ) : null}
    </div>
  );
};

export default CommunicationStrip;
